import { useSelector } from 'react-redux';
import {
  Box,
  Grid,
  List,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { makeStyles } from '@mui/styles';
import { isGTSelector } from '../../../store/Slices/companySlice';
import { IMsgReqEventDateType } from '../../../dto/MessageRequest.dto';
import { ICompanySchool } from '../../../dto/CompanySchool.dto';
import { IStation } from '../../../dto/Station.dto';
import { IEventType } from '../../../dto/EventType.dto';
import { IStartEndDateTime } from '../../../dto/DateTime.dto';
import { CombinedMessageProps } from './EventSpecificViewMessage';
import MusicPreviewBtn from '../../../components/music/MusicPreviewBtn';
import { commonDateFormat } from '../../../Service/CommonService';
import { useUserCanAny } from '../../../hooks/useUserCan';
import { CompanyShareSelection } from '../../../dto/MessageSharing.dto';
import ChildSharingReviewSummary from '../sharing/ChildSharingReviewSummary';

type Props = {
  schools: ICompanySchool[];
  stations: IStation[];
  events: IEventType[];
  event_date_type?: IMsgReqEventDateType;
  multiple_dates: IStartEndDateTime[];
  messages: CombinedMessageProps[];
  other_event_name?: string;
  from: string;
  created_date?: string;
  created_by?: string;
  days: string[];
  shareSelections?: CompanyShareSelection[];
};

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    overflowX: 'hidden',
  },
});

type ReviewFieldProps = {
  label: string;
  value: React.ReactNode;
  className?: string;
};

const ReviewField = ({label, value, className}: ReviewFieldProps) => {
  const classes = useStyles();
  return (
    <Box className={`${classes.root}`}>
      <Box className='w-150'>
        <Typography
          variant='h6'
          aria-level={2}
          gutterBottom
          className='text-white-50'>
          {label}
        </Typography>
      </Box>
      <Box className={`${className}`}>
        <Typography variant='h6' aria-level={2} className='text-white'>
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

const EsmReview = ({
  schools,
  stations,
  events,
  multiple_dates,
  messages,
  from,
  created_date,
  created_by,
  other_event_name,
  days,
  shareSelections,
}: Props) => {
  const isGT = useSelector(isGTSelector);
  const classes = useStyles();
  const isAdminAndViewStaff = useUserCanAny('is-admin,event-message:view');
  return (
    <>
      {from && from === 'form' && (
        <Grid item xs={12}>
          <Box>
            <Typography
              variant='h5'
              aria-level={2}
              className='text-white font-bold mt-2 mb-9'>
              Review
            </Typography>
          </Box>
        </Grid>
      )}
      <Grid item xs={12} tabIndex={0}>
        {created_date && created_by && (
          <Box className='flex justify-between mb-5 border-bottom-2'>
            <Box>
              <Typography
                variant='h6'
                aria-level={2}
                gutterBottom
                className='mb-0 text-white-50'>
                Created Date
              </Typography>
              <h5 className='text-white text-16' aria-level={2}>
                {dayjs(created_date).format('MM/DD/YYYY hh:mm:ss A')}
              </h5>
            </Box>
            <Box className='text-right'>
              <Typography
                variant='h6'
                aria-level={2}
                gutterBottom
                className='mb-0 text-white-50'>
                Created by
              </Typography>
              <h5 className='text-white text-16' aria-level={2}>
                {created_by}
              </h5>
            </Box>
          </Box>
        )}
        {isGT && (
          <Box className={`${classes.root} mb-4`}>
            <Box className='w-82'>
              <Typography
                variant='h6'
                aria-level={2}
                gutterBottom
                className='mb-0 text-white-50'>
                Schools
              </Typography>
            </Box>
            <Box>
              <Typography variant='h6' aria-level={2} className='text-white'>
                <Box
                  className={`${classes.root} scrollbar flex flex-wrap max-h-84 max-w-650`}
                  sx={{overflowY: 'hidden', gap: '2px'}}
                  tabIndex={0}>
                  {schools.map((item, index: number) => {
                    if (item.id !== 0) {
                      return (
                        <Box key={item.id}>
                          <span id='school-name'>{item.name}</span>
                          {index !== schools.length - 1 && <span>, </span>}
                        </Box>
                      );
                    }
                    return null;
                  })}
                </Box>
              </Typography>
            </Box>
          </Box>
        )}
        <Box className={classes.root} style={{marginBottom: '16px'}}>
          <Box className='w-82'>
            <Typography
              variant='h6'
              aria-level={2}
              gutterBottom
              className='mb-0 text-white-50'>
              Stations
            </Typography>
          </Box>
          <Box>
            <Typography variant='h6' aria-level={2} className='text-white'>
              <Box
                className={`${classes.root} scrollbar flex flex-wrap max-h-84 max-w-650 `}
                sx={{overflowY: 'hidden', gap: '2px'}}
                tabIndex={0}>
                {stations.map((item, index: number) => {
                  if (item.id !== 0) {
                    return (
                      <Box key={item.id}>
                        <span id='Stations-name'>
                          {item.label || item.name}
                        </span>
                        {index !== stations.length - 1 && <span>, </span>}
                      </Box>
                    );
                  }
                  return null;
                })}
              </Box>
            </Typography>
          </Box>
        </Box>
        {isGT && (
          <Box className={classes.root} style={{marginBottom: '16px'}}>
            <Box className='w-82'>
              <Typography
                variant='h6'
                aria-level={2}
                gutterBottom
                className='mb-0 text-white-50'>
                Sports
              </Typography>
            </Box>
            <Box>
              <Typography variant='h6' aria-level={2} className='text-white'>
                <Box
                  className={`${classes.root} scrollbar flex flex-wrap max-h-84 max-w-650 `}
                  sx={{overflowY: 'hidden', gap: '2px'}}
                  tabIndex={0}>
                  {events.map((item, index: number) => {
                    if (item.id !== 0) {
                      return (
                        <Box key={item.id}>
                          <span id='Sports-name' style={{whiteSpace: 'nowrap'}}>
                            {item.event_name}
                          </span>
                          {index !== stations.length - 1 && <span>, </span>}
                        </Box>
                      );
                    }
                    return null;
                  })}
                  {other_event_name &&
                    events.find(x => x.id === 0) &&
                    (events.length > 1
                      ? `, ${other_event_name}`
                      : other_event_name)}
                </Box>
              </Typography>
            </Box>
          </Box>
        )}
        <Box>
          <Typography variant='h6' aria-level={2} className='text-white'>
            Event Date
          </Typography>
          <TableContainer
            component={Paper}
            sx={{backgroundColor: 'transparent'}}
            className='indReviewTable pb-11'
            elevation={0}>
            <Table>
              <TableHead>
                <TableRow key={-1}>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Start Time</TableCell>
                  <TableCell>End Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {multiple_dates.map((row, index) => (
                  <TableRow key={row.id}>
                    <TableCell id={`start-date-${index + 1}`}>
                      {commonDateFormat(row.start_date)}
                    </TableCell>
                    <TableCell
                      id={`end-date-${index + 1}`}
                      className='text-white'>
                      {commonDateFormat(row.end_date)}
                    </TableCell>
                    <TableCell
                      id={`start-time-${index + 1}`}
                      className='text-white'>
                      {row.start_time?.format('hh:mm A')}
                    </TableCell>
                    <TableCell
                      id={`end-time-${index + 1}`}
                      className='text-white'>
                      {row.end_time?.format('hh:mm A')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Box mb={4}>
          <ReviewField
            label='Selected days'
            value={days
              .map(d => d.charAt(0).toUpperCase() + d.slice(1).toLowerCase())
              .join(', ')}
          />
          {isAdminAndViewStaff && from && from === 'view' && (
            <>
              <ReviewField
                label='Voice Artist'
                value={messages[0]?.voice_artist_name || 'NA'}
              />
              <ReviewField
                label='Message Creator'
                value={messages[0]?.message_creator_name || 'NA'}
              />
            </>
          )}
        </Box>

        {shareSelections && shareSelections.length > 0 && (
          <ChildSharingReviewSummary shareSelections={shareSelections} />
        )}

        <Box>
          <Typography variant='h6' aria-level={2} className='text-white'>
            Message Details
          </Typography>
          <TableContainer
            component={Paper}
            className='indReviewTable'
            elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell className='w-50'>Play</TableCell>
                  <TableCell className='min-w-160 text-white-50'>
                    Message Name
                  </TableCell>
                  <TableCell className='text-white-50'>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {messages.map(
                  (row, index) =>
                    row.message !== '' &&
                    row.name !== '' && (
                      <TableRow key={row.uuid}>
                        <TableCell align='center'>
                          {row.media_track ? (
                            <Stack direction='row' spacing={2}>
                              <MusicPreviewBtn
                                id={row.media_track.id}
                                url={row.media_track.url}
                                track_name={
                                  row.media_track.track_name || row.name
                                }
                                track_artist={
                                  row.media_track.track_artist || row.message
                                }
                                isDownloadable
                                downloadFileName={row.media_track.track_name}
                              />
                            </Stack>
                          ) : null}
                        </TableCell>
                        <TableCell
                          id={`message-name-${index + 1}`}
                          className='text-white'>
                          {row.name}
                        </TableCell>
                        <TableCell
                          id={`message-description-${index + 1}`}
                          className='text-white'>
                          {row.message}
                        </TableCell>
                      </TableRow>
                    ),
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Grid>
    </>
  );
};

export default EsmReview;

export const EsmDateTime = ({
  event_date_type,
  multiple_dates,
}: {
  event_date_type: IMsgReqEventDateType;
  multiple_dates: IStartEndDateTime[];
}) => {
  return (
    <>
      <Typography>
        {event_date_type === 1 ? 'Single Day' : 'Multiple Days'}
      </Typography>
      {event_date_type === 1 ? (
        <Typography>
          {multiple_dates.map(date => (
            <Typography key={date.id}>
              {date.start_date?.format('MM-DD-YYYY')} (
              {date.is_whole_day ? (
                'Whole day'
              ) : (
                <>
                  {date.start_time?.format('hh:mm A')} -{' '}
                  {date.end_time?.format('hh:mm A')}
                </>
              )}
              )
            </Typography>
          ))}
        </Typography>
      ) : (
        <List>
          <Table aria-label='simple table indReviewTable'>
            <TableHead>
              <TableRow>
                <TableCell>START DATE</TableCell>
                <TableCell>END DATE</TableCell>
                <TableCell>TIME</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {multiple_dates.map(date => (
                <TableRow key={date.id}>
                  <TableCell>{date.start_date?.format('MM-DD-YYYY')}</TableCell>
                  <TableCell>{date.end_date?.format('MM-DD-YYYY')}</TableCell>
                  <TableCell>
                    {date.is_whole_day ? (
                      'Whole day'
                    ) : (
                      <>
                        {date.start_time?.format('hh:mm A')} -{' '}
                        {date.end_time?.format('hh:mm A')}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </List>
      )}
    </>
  );
};
